// ⚠️  DOC-UPDATE: UI changes here may require updates to:
//     • client/public/combat-guide.html  (combat UI screenshots, flow descriptions)
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { setUserHasInteracted } from '../utils/audioState';
import { effectiveMusicVolume } from '../utils/settings';
import useKeybinds from '../hooks/useKeybinds';
import { getSpellPreview, getSpellTooltip, getAbilityPreview } from './shared/abilityScaling';
import './CombatInterface.css';
import './CombatModern.css';
import CardHand from './CardHand';
import ClassSystemGauge from './ClassSystemGauge';
import CombatTutorial from './CombatTutorial';

// Spell type display names and colors
const SPELL_TYPE_INFO = {
  DAMAGE: { label: 'Damage', color: '#e74c3c', icon: '✨' },
  HEAL: { label: 'Heal', color: '#27ae60', icon: '✨' },
  BUFF_ATT: { label: 'Buff Attack', color: '#f39c12', icon: '✨' },
  BUFF_DEF: { label: 'Buff Defense', color: '#3498db', icon: '✨' },
  BUFF_STAT: { label: 'Buff Stat', color: '#9b59b6', icon: '✨' },
  DEBUFF_DEF: { label: 'Debuff Def', color: '#8e44ad', icon: '✨' },
  DEBUFF_ATT: { label: 'Debuff Att', color: '#c0392b', icon: '✨' },
  DEBUFF_MISS: { label: 'Miss Chance', color: '#7f8c8d', icon: '✨' },
  HP_DIVIDE: { label: 'HP Divide', color: '#2c3e50', icon: '✨' },
  MIRROR_IMAGE: { label: 'Mirror Image', color: '#1abc9c', icon: '✨' },
  DIVINATION: { label: 'Divination', color: '#f1c40f', icon: '✨' },
  TRAVEL: { label: 'Travel', color: '#16a085', icon: '✨' },
  COMBO_DAMAGE: { label: 'Combo', color: '#e67e22', icon: '✨' },
  DRAIN: { label: 'Drain', color: '#8e44ad', icon: '✨' },
  COMBO_BUFF: { label: 'Combo Buff', color: '#2ecc71', icon: '✨' },
  COMBO_DEBUFF: { label: 'Combo Debuff', color: '#e74c3c', icon: '✨' },
  STAT_UP: { label: 'Stat Up', color: '#3498db', icon: '✨' },
  STAT_DOWN: { label: 'Stat Down', color: '#e74c3c', icon: '✨' }
};

// Element display info: icon + color for element badges on spells & combat log
const ELEMENT_INFO = {
  Fire:        { icon: '🔥', color: '#e74c3c' },
  Cold:        { icon: '❄️', color: '#3498db' },
  Ice:         { icon: '❄️', color: '#3498db' },
  Electricity: { icon: '⚡', color: '#f1c40f' },
  Lightning:   { icon: '⚡', color: '#f1c40f' },
  Acid:        { icon: '🧪', color: '#27ae60' },
  Water:       { icon: '💧', color: '#2980b9' },
  Earth:       { icon: '🪨', color: '#8B7355' },
  Air:         { icon: '🌪️', color: '#bdc3c7' },
  Sonic:       { icon: '🔊', color: '#9b59b6' },
  Dark:        { icon: '🌑', color: '#6c3483' },
  Light:       { icon: '☀️', color: '#f9e79f' },
  Holy:        { icon: '✝️', color: '#f9e79f' },
  Psionic:     { icon: '🧠', color: '#a569bd' },
  Arcane:      { icon: '💫', color: '#8e44ad' },
  Poison:      { icon: '☠️', color: '#2ecc71' },
  Healing:     { icon: '💚', color: '#27ae60' },
  Good:        { icon: '✝️', color: '#f1c40f' },
  Mind:        { icon: '🧠', color: '#a569bd' },
};

const CombatInterface = ({ battle, onAction, onClose, battleMusic, onTrack, onCombatError, modernLayout, characterPortrait, characterId }) => {
  const track = onTrack || (() => {}); // no-op fallback
  // v640.5: User-rebindable hotkey letters
  const { binds: keybinds } = useKeybinds();
  const [actionMode, setActionMode] = useState('main'); // main, spells, abilities, items
  const [selectedSpell, setSelectedSpell] = useState(null);
  const [selectedAbility, setSelectedAbility] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [spellFilter, setSpellFilter] = useState('all');
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const logRef = useRef(null);
  const battleAudioRef = useRef(null);
  const [lastAction, setLastAction] = useState(null);
  const [floatingNumbers, setFloatingNumbers] = useState([]);
  const [cardPlayError, setCardPlayError] = useState(null);
  const cardPlayErrorTimerRef = useRef(null); // v480 fix: track error dismiss timer (kept for back-compat; unused since v643.1)
  const [pendingCardId, setPendingCardId] = useState(null); // v643.1: visual feedback for in-flight card play
  // v700: Redraw potion (Phial of Foresight) UI state.
  // When non-null, the hand is in card-selection mode; user picks any number
  // of cards to discard, then confirms to draw the same number.
  const [redrawState, setRedrawState] = useState(null); // { itemName: string, ids: Set<string> } | null
  const prevBattleRef = useRef(null);
  const keyHandlerRef = useRef(null);
  const [turnTransition, setTurnTransition] = useState(null); // 'player' | 'monster' | null
  const [recentSpells, setRecentSpells] = useState([]); // track recently cast spell names for sorting
  const actionSentRef = useRef(false); // Prevents double-fire during network latency
  const actionTimeoutRef = useRef(null); // Safety timeout for actionSentRef
  const [showCombatTutorial, setShowCombatTutorial] = useState(() => !localStorage.getItem(`loria_combat_tutorial_seen_${characterId || 'default'}`));

  // Reset action lock when battle state changes (server responded).
  // battle._tick is a monotonic server counter — increments on every battle_update so
  // this effect fires even when round/turn/hp/logLength are all unchanged (e.g. attack miss
  // in a long fight where the log has been capped at MAX_LOG entries).
  useEffect(() => {
    actionSentRef.current = false;
    setPendingCardId(null); // v643.1: clear in-flight card glow once server responds
    if (actionTimeoutRef.current) {
      clearTimeout(actionTimeoutRef.current);
      actionTimeoutRef.current = null;
    }
    // Check if the latest log entry indicates a failed card play
    const log = battle?.log;
    if (log && log.length > 0) {
      const lastEntry = log[log.length - 1];
      if (typeof lastEntry === 'string' && (lastEntry.includes('Not enough MP') || lastEntry.includes('Cannot cast') || lastEntry.includes('action failed') || lastEntry.includes('Card was countered'))) {
        setCardPlayError(lastEntry);
        // v643.1: error persists until next card play or turn change (no auto-clear timer)
        // Clear any legacy timer to be safe
        if (cardPlayErrorTimerRef.current) {
          clearTimeout(cardPlayErrorTimerRef.current);
          cardPlayErrorTimerRef.current = null;
        }
      }
    }
  }, [battle?.round, battle?.turn, battle?.character?.currentHp, battle?.monster?.currentHp, battle?.log?.length, battle?._tick]);

  // Expose an imperative reset so GamePage can trigger it on combat_error
  useEffect(() => {
    if (onCombatError) {
      onCombatError.resetLock = () => {
        actionSentRef.current = false;
        setPendingCardId(null); // v643.1: clear in-flight glow when server rejects
        if (actionTimeoutRef.current) {
          clearTimeout(actionTimeoutRef.current);
          actionTimeoutRef.current = null;
        }
      };
    }
  });

  // Clean up timeout on unmount
  useEffect(() => {
    return () => { if (actionTimeoutRef.current) clearTimeout(actionTimeoutRef.current); };
  }, []);

  // Guarded action dispatch — prevents rapid re-fire
  const safeAction = (action) => {
    // Don't send actions once the fight is over (victory/defeat/fled).
    // battle_update(status:victory) resets actionSentRef via _tick but the
    // battle_ended event may not have arrived yet — suppress stray clicks.
    if (battle?.status && battle.status !== 'active') return;
    if (actionSentRef.current) return;
    actionSentRef.current = true;
    // Safety: auto-reset after 3s if server never responds (combat_error, rate-limit, etc.)
    if (actionTimeoutRef.current) clearTimeout(actionTimeoutRef.current);
    actionTimeoutRef.current = setTimeout(() => {
      actionSentRef.current = false;
      actionTimeoutRef.current = null;
    }, 3000);
    onAction(action);
  };

  // Auto-scroll battle log to bottom
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [battle?.log]);

  // Reset to main menu on new turn
  useEffect(() => {
    if (battle?.turn === 'player' && battle?.status === 'active') {
      setActionMode('main');
      setSelectedSpell(null);
      setSelectedAbility(null);
      setSelectedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battle?.round]);

  // Turn transition animation  flash banner when turn changes
  useEffect(() => {
    if (!battle || battle.status !== 'active') return;
    setTurnTransition(battle.turn);
    const timer = setTimeout(() => setTurnTransition(null), 900);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battle?.turn, battle?.round]);

  // Battle music management  auto-play when battle starts
  useEffect(() => {
    const audio = battleAudioRef.current;
    if (!audio || !battleMusic) return;

    const musicPath = `/music/${battleMusic}`;
    if (audio.src !== window.location.origin + musicPath) {
      audio.src = musicPath;
      audio.volume = effectiveMusicVolume();
      audio.loop = true;
    }

    // Always attempt auto-play
    audio.play().then(() => {
      setIsMusicPlaying(true);
      setUserHasInteracted(true);
    }).catch(() => {
      setIsMusicPlaying(false);
    });

    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, [battleMusic]);

  // Keyboard shortcut listener (uses ref to avoid stale closures)
  useEffect(() => {
    const handler = (e) => { if (keyHandlerRef.current) keyHandlerRef.current(e); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Floating damage numbers  detect HP changes between renders
  const floatTimersRef = useRef([]);
  useEffect(() => {
    // Cleanup all floating number timers on unmount
    return () => {
      floatTimersRef.current.forEach(t => clearTimeout(t));
      floatTimersRef.current = [];
    };
  }, []);
  useEffect(() => {
    if (!battle) { prevBattleRef.current = null; return; }
    const prev = prevBattleRef.current;
    if (prev) {
      const newFloats = [];
      const now = Date.now();
      if (battle.monster.currentHp < prev.monHp) {
        newFloats.push({ id: now, value: prev.monHp - battle.monster.currentHp, type: 'damage', target: 'monster' });
      }
      if (battle.character.currentHp < prev.charHp) {
        newFloats.push({ id: now + 1, value: prev.charHp - battle.character.currentHp, type: 'damage', target: 'player' });
      }
      if (battle.character.currentHp > prev.charHp) {
        newFloats.push({ id: now + 2, value: battle.character.currentHp - prev.charHp, type: 'heal', target: 'player' });
      }
      if (battle.pet && prev.petHp != null && battle.pet.currentHp < prev.petHp) {
        newFloats.push({ id: now + 3, value: prev.petHp - battle.pet.currentHp, type: 'damage', target: 'pet' });
      }
      if (newFloats.length > 0) {
        setFloatingNumbers(old => [...old, ...newFloats]);
        const timer = setTimeout(() => {
          const ids = new Set(newFloats.map(f => f.id));
          setFloatingNumbers(old => old.filter(f => !ids.has(f.id)));
          // v480 fix: remove fired timer from ref to prevent unbounded growth (bug #45)
          floatTimersRef.current = floatTimersRef.current.filter(t => t !== timer);
        }, 1500);
        floatTimersRef.current.push(timer);
      }
    }
    prevBattleRef.current = {
      monHp: battle.monster.currentHp,
      charHp: battle.character.currentHp,
      petHp: battle.pet?.currentHp ?? null
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [battle?.monster?.currentHp, battle?.character?.currentHp, battle?.pet?.currentHp, battle?.round]);

  // ── Hooks that must be called unconditionally (before any early return) ──

  // Group spells by type for the spell picker (memoized)
  const groupedSpells = useMemo(() => {
    const groups = {};
    if (battle?.availableSpells) {
      battle.availableSpells
        .filter(spell => !spell.fellowshipOnly)
        .forEach(spell => {
        const type = spell.type || 'DAMAGE';
        if (!groups[type]) groups[type] = [];
        groups[type].push(spell);
      });
    }
    return groups;
  }, [battle?.availableSpells]);

  // Parse battle inventory for usable items (memoized)
  const battleItems = useMemo(() =>
    (battle?.inventory || []).filter(item =>
      (item.type === 'restore' || item.type === 'scroll' || item.type === 'buff' || item.type === 'redraw') && (item.quantity || 1) > 0
    ), [battle?.inventory]);

  // Classify a log message for color-coding (stable ref — no deps)
  const classifyLogMessage = useCallback((msg) => {
    if (/🛡️ Block|🛡️ All damage blocked/.test(msg)) return 'log-buff';
    if (/🃏 Deck built/.test(msg)) return 'log-encounter';
    if (/📤 Drew/.test(msg)) return 'log-buff';
    if (/⚡ Gained.*AP/.test(msg)) return 'log-buff';
    if (/🔮 Scrying/.test(msg)) return 'log-buff';
    if (/🎲 Chaos Surge/.test(msg)) return 'log-spell-damage';
    if (/⚖️ Equilibrium/.test(msg)) return 'log-buff';
    if (/💢 You take.*recoil/.test(msg)) return 'log-monster-damage';
    if (/🌑 You melt/.test(msg)) return 'log-buff';
    if (/👁️ Third Eye/.test(msg)) return 'log-buff';
    if (/🩸 Drain heals/.test(msg)) return 'log-heal';
    if (/💡 TIP:/.test(msg)) return 'log-tip';
    if (/COMBO:/.test(msg)) return 'log-combo';
    if (/super effective/.test(msg)) return 'log-weakness';
    if (/resists \w/.test(msg)) return 'log-resistance';
    if (/IMMUNE to/.test(msg)) return 'log-immune';
    if (/\+\d+\s+\w+\s+damage!$/.test(msg)) return 'log-elemental';
    if (/🧪 Poison deals/.test(msg)) return 'log-poison';
    if (/is stunned and cannot act|You are stunned/.test(msg)) return 'log-stun';
    if (/CRITICAL|INSTANT KILL/.test(msg)) return 'log-critical';
    if (/🐾|🐺|🐻|🦅|🐍|Your pet |Pet attacks|companion strikes|familiar/.test(msg)) return 'log-pet';
    if (/You attack .* for \d+ damage|Precise Stab|Sneak Attack|Recoil Strike|Critical Blow|Death Strike|Assassin Strike/.test(msg)) return 'log-player-damage';
    if (/You cast .* for \d+.*damage|Drain \d+/.test(msg)) return 'log-spell-damage';
    if (/heal for|restore|Miracle/i.test(msg)) return 'log-heal';
    if (/ATT \+|DEF \+|Mirror images|activated|Third Eye|Boost|Mana Fountain|Power Cast|Spontaneous|shadows/.test(msg)) return 'log-buff';
    if (/Enemy DEF|Enemy ATT|miss chance|entangled|STR -|MEN -/.test(msg)) return 'log-debuff';
    if (/Retribution|War Cry|Mind Blade|Spirit Dance|Wild Shape|Imbue|Holy Smite|Death Attack|Called Shot|Phantasmal|Horrifying|Mana Drain|Soul Breaker|Critical Assault|Divine Wrath|Nature's Wrath|Arcane Psionics|Grand Ritual|Alter Reality|Doom|Magnify Spell|Mind Over Magic|Arcane Prayer|Theurgic|Psychic Prayer|Primal Psionics|Lay on Hands|Elemental Fusion|Summon Elemental|Nature's Might|Psychic Strike|Twist of Fate/.test(msg)) return 'log-ac-ability';
    if (/Aura|aura|regen:/.test(msg)) return 'log-aura';
    if (/Spin the Wheel|Probability Cascade|Fate's Shield|Lucky/.test(msg)) return 'log-fatespinner';
    if (/Dance \(|Leaf Storm|Rainfall|Earthquake|Deluge|Lightning|Fortify|Stone Skin/.test(msg)) return 'log-dance';
    if (/breathes|exhales|hurls|unleashes|fires a|reaches out|blasts you|sprays|slams|wraps|drains your|sinks|injects|splashes|flies into|hardens|enters a frenzy|lets out|curses you|emits a|clouds your|splits into/.test(msg)) return 'log-monster-ability';
    if (/attacks.*for \d+ damage/.test(msg)) return 'log-monster-damage';
    if (/but miss|misses|hits a mirror image|you dodge/.test(msg)) return 'log-miss';
    if (/Steal succeeds|pilfer/.test(msg)) return 'log-steal';
    if (/You use /.test(msg)) return 'log-item';
    if (/defeated!|gain \d+ XP|Loot:|Seed XP/.test(msg)) return 'log-victory';
    if (/been defeated|been slain/.test(msg)) return 'log-defeat';
    if (/fled|escape|Failed to flee/.test(msg)) return 'log-flee';
    if (/encountered a/.test(msg)) return 'log-encounter';
    if (/Charm|Friendship|companion/.test(msg)) return 'log-charm';
    return 'log-default';
  }, []);

  // Sort spells with recently used first (memoized)
  const filteredSpellsRaw = spellFilter === 'all'
    ? (battle?.availableSpells || []).filter(s => !s.fellowshipOnly)
    : (groupedSpells[spellFilter] || []);
  const sortedFilteredSpells = useMemo(() => [...filteredSpellsRaw].sort((a, b) => {
    const aIdx = recentSpells.indexOf(a.name);
    const bIdx = recentSpells.indexOf(b.name);
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return 0;
  }), [filteredSpellsRaw, recentSpells]);

  const toggleBattleMusic = () => {
    const audio = battleAudioRef.current;
    if (!audio) return;

    if (isMusicPlaying) {
      audio.pause();
      setIsMusicPlaying(false);
    } else {
      audio.play().then(() => {
        setIsMusicPlaying(true);
        setUserHasInteracted(true);
      }).catch(err => {
        console.warn('Could not play battle music:', err);
      });
    }
  };

  if (!battle) return null;

  const { character, monster } = battle;

  // v643.2: helper for spell-vs-monster element badge (RESISTED / WEAK / IMMUNE)
  const getResistBadge = (spell) => {
    const el = spell?.element || spell?._spellMeta?.element || spell?.effects?.magicDamage?.element;
    if (!el || !monster) return null;
    if (Array.isArray(monster.immunities) && monster.immunities.includes(el)) {
      return { text: 'IMMUNE', color: '#ef4444', bg: 'rgba(239,68,68,0.18)', title: `${monster.name} is immune to ${el}` };
    }
    const r = monster.resistances?.[el];
    if (typeof r === 'number' && r > 0 && r < 1) {
      return { text: 'RESISTED', color: '#f59e0b', bg: 'rgba(245,158,11,0.16)', title: `${monster.name} resists ${el} (×${r})` };
    }
    const w = monster.weaknesses?.[el];
    if (typeof w === 'number' && w > 1) {
      return { text: 'WEAK', color: '#22c55e', bg: 'rgba(34,197,94,0.18)', title: `${monster.name} is weak to ${el} (×${w})` };
    }
    return null;
  };

  // Health/mana percentages
  const charHpPct = character.hp > 0 ? Math.max(0, (character.currentHp / character.hp) * 100) : 0;
  const monHpPct = monster.hp > 0 ? Math.max(0, (monster.currentHp / monster.hp) * 100) : 0;
  const charMpPct = character.mp > 0 ? Math.max(0, (character.currentMp / character.mp) * 100) : 0;
  
  // Pet data
  const pet = battle.pet;
  const petHpPct = pet && pet.hp > 0 ? Math.max(0, (pet.currentHp / pet.hp) * 100) : 0;

  const isPlayerTurn = battle.turn === 'player' && battle.status === 'active';

  const spellTypes = Object.keys(groupedSpells);
  const filteredSpells = filteredSpellsRaw;

  const canAffordSpell = (spell) => character.currentMp >= spell.mp;

  // Check if the last action can be repeated (e.g., spell still affordable)
  const canRepeatLastAction = () => {
    if (!lastAction) return false;
    if (lastAction.type === 'spell') {
      const spell = battle.availableSpells?.find(s => s.name === lastAction.spell);
      return spell && canAffordSpell(spell);
    }
    if (lastAction.type === 'item') {
      const item = (battle.inventory || []).find(i => i.name === lastAction.item && (i.quantity || 1) > 0);
      return !!item;
    }
    return true;
  };

  // Action handlers
  const handleAttack = () => {
    const action = { type: 'attack' };
    track('btn_attack', 'Attack');
    safeAction(action);
    setLastAction({ ...action, label: 'Attack' });
    setActionMode('main');
  };

  const handleCastSpell = (spell) => {
    if (!canAffordSpell(spell)) return;
    const action = { type: 'spell', spell: spell.name };
    track('btn_spell', `${spell.name} (${spell.mp || '?'}mp)`);
    safeAction(action);
    setLastAction({ ...action, label: spell.name });
    // Track recently used spells for auto-sorting (#10)
    setRecentSpells(prev => {
      const filtered = prev.filter(n => n !== spell.name);
      return [spell.name, ...filtered].slice(0, 20);
    });
    setActionMode('main');
    setSelectedSpell(null);
  };

  const handleUseAbility = (ability) => {
    // For skill tree abilities, send the skillKey; for class abilities, send the name
    const abilityId = ability.skillKey || ability.name;
    const action = { type: 'ability', ability: abilityId };
    track('btn_ability', ability.name);
    safeAction(action);
    setLastAction({ ...action, label: ability.name });
    setActionMode('main');
    setSelectedAbility(null);
  };

  const handleRun = () => {
    track('btn_run', 'Run');
    safeAction({ type: 'run' });
    setActionMode('main');
  };

  const handleUseItem = (item) => {
    // v700: Redraw consumables (Phial of Foresight) need a card-pick step.
    if (item.type === 'redraw') {
      track('btn_item_redraw_open', item.name);
      setRedrawState({ itemName: item.name, ids: new Set() });
      setActionMode('main');
      setSelectedItem(null);
      return;
    }
    const action = { type: 'item', item: item.name };
    track('btn_item', item.name);
    safeAction(action);
    setLastAction({ ...action, label: item.name });
    setActionMode('main');
    setSelectedItem(null);
  };

  // v700: Redraw potion handlers
  const handleRedrawToggle = (cardId) => {
    setRedrawState(prev => {
      if (!prev) return prev;
      const next = new Set(prev.ids);
      if (next.has(cardId)) next.delete(cardId);
      else next.add(cardId);
      return { ...prev, ids: next };
    });
  };
  const handleRedrawConfirm = () => {
    if (!redrawState || redrawState.ids.size === 0) return;
    const cardIds = Array.from(redrawState.ids);
    const action = { type: 'item', item: redrawState.itemName, cardIds };
    track('btn_item_redraw_confirm', `${redrawState.itemName} ×${cardIds.length}`);
    safeAction(action);
    setLastAction({ ...action, label: redrawState.itemName });
    setRedrawState(null);
  };
  const handleRedrawCancel = () => {
    track('btn_item_redraw_cancel', redrawState?.itemName || '?');
    setRedrawState(null);
  };

  // ── Deck-Building Card System Handlers ──
  const handlePlayCard = (cardId) => {
    const card = battle?.deck?.hand?.find(c => c.id === cardId);
    track('card_play', `${card?.name || '?'} (id:${cardId} cost:${card?.apCost ?? '?'}AP)`);
    // v643.1: clear stale error and mark this card as in-flight for visual feedback
    setCardPlayError(null);
    setPendingCardId(cardId);
    safeAction({ type: 'play_card', cardId });
  };

  const handleEndTurn = () => {
    track('btn_end_turn', `R${battle?.round || '?'} AP:${battle?.deck?.ap ?? '?'}/${battle?.deck?.maxAp ?? '?'} hand:${battle?.deck?.hand?.length || 0}`);
    safeAction({ type: 'end_turn' });
  };

  const handleScryResolve = (topIds, bottomIds) => {
    safeAction({ type: 'scry_resolve', topIds, bottomIds });
  };

  const handleBaselineScry = () => {
    track('btn_baseline_scry', `R${battle?.round || '?'}`);
    safeAction({ type: 'baseline_scry' });
  };

  const handleExhaustResolve = (cardIds) => {
    safeAction({ type: 'exhaust_resolve', cardIds });
  };

  const handleDeckExhaustResolve = (cardIds) => {
    safeAction({ type: 'deck_exhaust_resolve', cardIds });
  };

  // battleItems already computed above (before early return)

  const getStatusMessage = () => {
    switch (battle.status) {
      case 'victory': return '🏆 Victory! You have defeated your enemy!';
      case 'defeat': return '💀 Defeat! You have been slain...';
      case 'fled': return '✨ You have successfully fled from battle!';
      default:
        return battle.turn === 'player'
          ? `Round ${battle.round}  Your turn!`
          : `Round ${battle.round}  ${monster.name}'s turn...`;
    }
  };

  // ---- QoL Helpers ----

  // classifyLogMessage already computed above (before early return)

  // Dynamic HP bar gradient based on percentage
  const getHpBarGradient = (pct) => {
    if (pct > 60) return 'linear-gradient(90deg, #27ae60, #2ecc71)';
    if (pct > 30) return 'linear-gradient(90deg, #f39c12, #e67e22)';
    return 'linear-gradient(90deg, #e74c3c, #c0392b)';
  };

  // sortedFilteredSpells already computed above (before early return)

  // #11: Low HP danger detection (no sound, visual only)
  const isLowHp = charHpPct > 0 && charHpPct <= 25;
  const isDangerHp = charHpPct > 0 && charHpPct <= 15;

  // Update keyboard handler ref (captures current closure values each render)
  keyHandlerRef.current = (e) => {
    if (!isPlayerTurn || battle.status !== 'active') return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    const key = e.key.toLowerCase();

    if (actionMode === 'main') {
      if (battle.deck) {
        // Deck mode shortcuts
        if (key === keybinds.endTurn || key === ' ') { e.preventDefault(); handleEndTurn(); }
        else if ((key === keybinds.combatItems) && battleItems.length > 0) { e.preventDefault(); setActionMode('items'); }
        else if ((key === keybinds.run) && !battle?.noRun) { e.preventDefault(); handleRun(); }
      } else {
        // Classic mode shortcuts
        if (key === keybinds.attack || key === '1') { e.preventDefault(); handleAttack(); }
        else if ((key === keybinds.combatSpells || key === '2') && battle.availableSpells?.length > 0) { e.preventDefault(); setActionMode('spells'); }
        else if ((key === keybinds.abilities || key === '3') && battle.availableAbilities?.length > 0) { e.preventDefault(); setActionMode('abilities'); }
        else if ((key === keybinds.combatItems || key === '4') && battleItems.length > 0) { e.preventDefault(); setActionMode('items'); }
        else if ((key === keybinds.run || key === '5') && !battle?.noRun) { e.preventDefault(); handleRun(); }
        else if (key === keybinds.repeatLast && lastAction && canRepeatLastAction()) { e.preventDefault(); const { label, ...act } = lastAction; safeAction(act); }
      }
    } else {
      if (key === 'escape' || key === 'backspace') { e.preventDefault(); setActionMode('main'); return; }
      const num = parseInt(e.key);
      if (actionMode === 'spells') {
        if (key === 'enter' && selectedSpell && canAffordSpell(selectedSpell)) { e.preventDefault(); handleCastSpell(selectedSpell); }
        else if (!isNaN(num) && num >= 1 && num <= sortedFilteredSpells.length) {
          e.preventDefault();
          const spell = sortedFilteredSpells[num - 1];
          if (canAffordSpell(spell)) handleCastSpell(spell);
        }
      } else if (actionMode === 'abilities') {
        if (key === 'enter' && selectedAbility) { e.preventDefault(); handleUseAbility(selectedAbility); }
        else if (!isNaN(num) && num >= 1 && num <= battle.availableAbilities.length) {
          e.preventDefault(); handleUseAbility(battle.availableAbilities[num - 1]);
        }
      } else if (actionMode === 'items') {
        if (key === 'enter' && selectedItem) { e.preventDefault(); handleUseItem(selectedItem); }
        else if (!isNaN(num) && num >= 1 && num <= battleItems.length) {
          e.preventDefault(); handleUseItem(battleItems[num - 1]);
        }
      }
    }
  };

  // Render active buffs/debuffs
  const renderEffects = (entity, label) => {
    const effects = [
      ...(entity.buffs || []).map(b => ({ ...b, _isBuff: true })),
      ...(entity.debuffs || []).map(b => ({ ...b, _isBuff: false })),
    ];
    if (effects.length === 0) return null;
    return (
      <div className="combat-effects">
        {effects.map((eff, i) => (
          <span key={i} className={`effect-tag ${eff._isBuff ? 'buff' : 'debuff'}`}>
            {eff.name || eff.type} ({eff.rounds || eff.duration || '∞'}t)
          </span>
        ))}
      </div>
    );
  };

  // ════════════════════════════════════════════════════════════
  //  MODERN CINEMATIC LAYOUT
  // ════════════════════════════════════════════════════════════
  if (modernLayout) {
    const hpClass = monHpPct > 60 ? 'hp' : monHpPct > 30 ? 'hp-med' : 'hp-low';
    const charHpClass = charHpPct > 60 ? 'hp' : charHpPct > 30 ? 'hp-med' : 'hp-low';
    const deckState = battle.deck;

    // Boss mechanic renderer (reused)
    const renderBossMechanic = () => {
      if (!monster._bossMechanic) return null;
      const bm = monster._bossMechanic;
      const mechType = bm.type;
      let icon = '⚙️', label = '', detail = '';
      if (mechType === 'shell') {
        icon = bm._shellActive ? '🛡️' : '💥';
        label = bm._shellActive ? 'Armored Shell' : 'Shell Broken!';
        detail = bm._shellActive ? `DR +${bm.shellDR} — Deal ${bm.breakThreshold}+ to shatter!` : `Vulnerable! ×${bm.vulnerabilityDamageMult || 1.5} dmg for ${bm._vulnerableTurns}t`;
      } else if (mechType === 'escalate') {
        icon = '📈'; label = `${bm.stackBuffName || 'Escalation'} x${bm._stacks || 0}`;
        detail = `DEF +${bm._totalDef || 0} / DR +${bm._totalDR || 0} (max ${bm.maxStacks || 8})`;
      } else if (mechType === 'charge_up') {
        icon = '⚡'; label = `Charging: ${bm._stacks || 0} / ${bm.maxStacks || 3}`;
        detail = bm._stacks >= (bm.maxStacks || 3) - 1 ? '⚠️ NEXT TURN: Devastating attack!' : 'Building power...';
      } else if (mechType === 'crushing_weight') {
        icon = '🏋️'; label = `Weight: ${bm._stacks || 0} / ${bm.crushThreshold || 5}`;
        detail = bm._stacks >= (bm.crushThreshold || 5) - 1 ? '⚠️ NEXT TURN: Crushing blow!' : `DEF +${bm._totalDef || 0} / DR +${bm._totalDR || 0}`;
      } else if (mechType === 'multi_action') {
        icon = '⚔️'; label = 'Multi-Action'; detail = 'Attacks twice per turn!';
      }
      const extra = (mechType === 'shell' && !bm._shellActive) ? ' vulnerable' : (mechType === 'charge_up' && bm._stacks >= (bm.maxStacks || 3) - 1) || (mechType === 'crushing_weight' && bm._stacks >= (bm.crushThreshold || 5) - 1) ? ' warning' : '';
      return (
        <div className={`cm-boss-mech${extra}`}>
          <span className="mechanic-icon">{icon}</span>
          <span className="mechanic-label">{label}</span>
          <span className="mechanic-detail">{detail}</span>
        </div>
      );
    };

    return (
      <div className="cm-root">
        <audio ref={battleAudioRef} loop />

        {/* Monster art backdrop */}
        <div className="cm-backdrop">
          <img
            src={`/images/monsters/${monster.pic}`}
            alt=""
            onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
          />
        </div>

        {/* Danger vignettes */}
        {isLowHp && <div className={`cm-vignette-low ${isDangerHp ? 'cm-vignette-critical' : ''}`} />}

        {/* Combat Tutorial (first fight) */}
        {showCombatTutorial && <CombatTutorial onComplete={() => setShowCombatTutorial(false)} characterId={characterId} />}
        {turnTransition && (
          <div className={`cm-turn-banner turn-${turnTransition}`}>
            {turnTransition === 'player' ? '✨ Your Turn!' : `${monster.name}'s Turn`}
          </div>
        )}

        <div className="cm-content">
          {/* ── Top bar ── */}
          <div className="cm-topbar">
            <div className="cm-round-info">
              <span className="cm-round-label">Round</span>
              <span className="cm-round-num">{battle.round}</span>
            </div>
            <div className="cm-top-right">
              {battleMusic && (
                <button className="cm-music-btn" onClick={toggleBattleMusic} title={isMusicPlaying ? 'Pause' : 'Play'}>
                  {isMusicPlaying ? '🔊' : '🔇'}
                </button>
              )}
              {!battle.noRun && battle.status === 'active' && (
                <button className="cm-run-btn" onClick={handleRun} disabled={!isPlayerTurn}>
                  🏃 Run
                </button>
              )}
            </div>
          </div>

          {/* ── Middle: log left, monster info right ── */}
          <div className="cm-middle">
            {/* Combat Log */}
            <div className="cm-log">
              <div className="cm-log-title">
                ⚔️ Battle Log
                <span
                  className="cm-log-legend"
                  title={'Combat Log Color Legend:\n\n🟥 Red = damage you take / monster crits\n🟩 Green = heals & buffs\n🟦 Blue = your damage / spells\n🟧 Orange = elemental hits / weakness\n🟪 Purple = debuffs, stuns, charms\n🟨 Yellow = abilities, auras, dance, fate\n⚪ Grey = misses, flees, neutral events\n\nWatch for ★ on the latest line.'}
                  aria-label="Combat log legend"
                >ⓘ</span>
              </div>
              <div className="cm-log-messages" ref={logRef}>
                {battle.log.map((message, index) => (
                  <div key={index} className={`log-message ${classifyLogMessage(message)} ${index === battle.log.length - 1 ? 'latest' : ''}`}>
                    {message}
                  </div>
                ))}
              </div>
            </div>

            {/* Monster Info Panel */}
            <div className="cm-monster-panel">
              <div className="cm-floats">
                {floatingNumbers.filter(f => f.target === 'monster').map(f => (
                  <div key={f.id} className={`floating-number floating-${f.type}`}>
                    {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
                  </div>
                ))}
              </div>
              <div className="cm-monster-name">{monster.name}</div>
              <div>
                <span className="cm-monster-level">Level {monster.level}</span>
                {monster.type && <span className="cm-monster-type" style={{ marginLeft: 8 }}>{monster.type}</span>}
              </div>
              <div className="cm-monster-hp cm-bar-container">
                <span className="cm-bar-label">HP</span>
                <div className="cm-bar">
                  <div className={`cm-bar-fill ${hpClass}`} style={{ width: `${monHpPct}%` }} />
                  <span className="cm-bar-text">{monster.currentHp} / {monster.hp}</span>
                </div>
              </div>
              {renderBossMechanic()}
              {renderEffects(monster, 'Monster')}
              {monster.missChance > 0 && <div className="cm-indicator">Miss chance: {monster.missChance}%</div>}
              {monster.mirrorImages > 0 && <div className="cm-indicator">Mirror images: {monster.mirrorImages}</div>}
              {/* v622: elemental weakness/resist/immunity pills */}
              {(() => {
                const weakKeys = monster.weaknesses ? Object.keys(monster.weaknesses) : [];
                const resistKeys = monster.resistances ? Object.keys(monster.resistances) : [];
                const immuneKeys = Array.isArray(monster.immunities) ? monster.immunities : [];
                if (!weakKeys.length && !resistKeys.length && !immuneKeys.length) return null;
                const ELEM_ICON = { fire: '🔥', cold: '❄️', ice: '❄️', water: '💧', earth: '🪨', air: '💨', lightning: '⚡', electric: '⚡', poison: '☠️', acid: '🧪', necrotic: '💀', holy: '✨', radiant: '✨', psychic: '🧠', force: '💥', nature: '🌿' };
                const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
                return (
                  <div className="cm-elem-pills">
                    {weakKeys.map(el => (
                      <span key={'w-'+el} className="cm-elem-pill cm-elem-weak" title={`Weak to ${cap(el)} (×${monster.weaknesses[el]})`}>
                        {ELEM_ICON[el] || '⚡'} Weak: {cap(el)}
                      </span>
                    ))}
                    {resistKeys.map(el => (
                      <span key={'r-'+el} className="cm-elem-pill cm-elem-resist" title={`Resists ${cap(el)} (×${monster.resistances[el]})`}>
                        {ELEM_ICON[el] || '🛡️'} Resist: {cap(el)}
                      </span>
                    ))}
                    {immuneKeys.map(el => (
                      <span key={'i-'+el} className="cm-elem-pill cm-elem-immune" title={`Immune to ${cap(el)}`}>
                        🚫 Immune: {cap(el)}
                      </span>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* ── Bottom HUD ── */}

          {/* AC Status bar */}
          {battle.status === 'active' && (battle.activeMetamagic || battle.spiritDanceActive || battle.mindBladeActive ||
           battle.wildShapeActive || battle.doomActive || battle.retributionActive || battle.criticalAssaultActive ||
           battle.divineWrathActive || battle.soulBreakerActive || battle.naturesWrathActive || battle.arcanePsionicsActive ||
           battle.magnifySpellActive || battle.convergenceActive || battle.imbueMode ||
           (battle.imbueEffects && battle.imbueEffects.length > 0)) && (
            <div className="cm-ac-status" style={{ padding: '0 24px', justifyContent: 'center' }}>
              {battle.activeMetamagic && <span className="ac-badge ac-badge-metamagic">✨ Metamagic: {battle.metamagicOptions?.[battle.activeMetamagic]?.name || battle.activeMetamagic}</span>}
              {battle.spiritDanceActive && <span className="ac-badge ac-badge-dance">💃 Dancing: {battle.spiritDanceType}</span>}
              {battle.mindBladeActive && <span className="ac-badge ac-badge-mind">🧠 Mind Blade ({battle.mindBladeRounds}r)</span>}
              {battle.wildShapeActive && <span className="ac-badge ac-badge-nature">🐺 Wild Shape ({battle.wildShapeRounds}r)</span>}
              {battle.doomActive && <span className="ac-badge ac-badge-doom">☠️ Doom ({battle.doomRounds}r)</span>}
              {battle.retributionActive && <span className="ac-badge ac-badge-retribution">⚔️ Retribution ({battle.retributionRounds}r)</span>}
              {battle.criticalAssaultActive && <span className="ac-badge ac-badge-critical">💀 Critical Assault ({battle.criticalAssaultRounds}r)</span>}
              {battle.divineWrathActive && <span className="ac-badge ac-badge-divine">🛡️ Divine Wrath ({battle.divineWrathRounds}r)</span>}
              {battle.soulBreakerActive && <span className="ac-badge ac-badge-soul">💥 Soul Breaker ({battle.soulBreakerRounds}r)</span>}
              {battle.naturesWrathActive && <span className="ac-badge ac-badge-natureswrath">🌿 Nature's Wrath ({battle.naturesWrathRounds}r)</span>}
              {battle.arcanePsionicsActive && <span className="ac-badge ac-badge-arcane">⚡ Arcane Psionics ({battle.arcanePsionicsRounds}r)</span>}
              {battle.magnifySpellActive && <span className="ac-badge ac-badge-doom">⚡ Magnify Spell (next)</span>}
              {battle.convergenceActive && <span className="ac-badge ac-badge-divine">✨ {battle.convergenceType === 'arcane_prayer' ? 'Arcane Prayer' : 'Theurgic Convergence'} (next spell)</span>}
              {battle.imbueMode && <span className="ac-badge ac-badge-imbue">⚔️🔮 Imbue Mode — cast a damage spell!</span>}
              {battle.imbueEffects && battle.imbueEffects.map((imbue, idx) => (
                <span key={`imbue-${idx}`} className="ac-badge ac-badge-retribution">⚔️🔮 {imbue.spellName} ({imbue.element}) — {imbue.rounds}r</span>
              ))}
            </div>
          )}

          {/* Metamagic toggles */}
          {battle.status === 'active' && battle.metamagicOptions && Object.keys(battle.metamagicOptions).length > 0 && isPlayerTurn && (
            <div className="cm-metamagic" style={{ padding: '4px 24px' }}>
              <span className="metamagic-label">Metamagic:</span>
              {Object.entries(battle.metamagicOptions).map(([key, mm]) => (
                <button key={key} className={`metamagic-btn${battle.activeMetamagic === key ? ' active' : ''}`}
                  onClick={() => onAction({ type: 'metamagic_toggle', metamagicKey: key })}
                  title={`${mm.name || key}: ×${mm.mpMultiplier || 1} MP cost`}>
                  {battle.activeMetamagic === key ? '✨ ' : ''}{mm.name || key} (×{mm.mpMultiplier || 1} MP)
                </button>
              ))}
            </div>
          )}

          {/* Class system gauge */}
          {battle.status === 'active' && battle.classSystemState && (
            <div className="cm-gauge-row">
              <ClassSystemGauge classSystemState={battle.classSystemState} onAction={onAction} isPlayerTurn={isPlayerTurn} />
            </div>
          )}

          {/* Battle ended */}
          {battle.status !== 'active' && (
            <div className="cm-end-panel">
              <p>{getStatusMessage()}</p>
              <button className="cm-continue-btn" onClick={onClose}>Continue</button>
            </div>
          )}

          {/* Active battle bottom HUD */}
          {battle.status === 'active' && (
            <div className="cm-bottom">
              {/* Character info — bottom left */}
              <div className="cm-character">
                <div className="cm-portrait-frame">
                  {characterPortrait ? (
                    <img src={characterPortrait} alt="" />
                  ) : (
                    <div className="cm-portrait-placeholder">⚔️</div>
                  )}
                </div>
                <div className="cm-char-stats">
                  <div className="cm-floats">
                    {floatingNumbers.filter(f => f.target === 'player').map(f => (
                      <div key={f.id} className={`floating-number floating-${f.type}`}>
                        {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
                      </div>
                    ))}
                  </div>
                  <div className="cm-char-name">{character.name}</div>
                  <div className="cm-char-class">{character.advanced_class || character.class} — Level {character.level}</div>
                  <div className="cm-bar-container">
                    <span className="cm-bar-label">HP</span>
                    <div className="cm-bar">
                      <div className={`cm-bar-fill ${charHpClass}`} style={{ width: `${charHpPct}%` }} />
                      <span className="cm-bar-text">{Math.max(0, character.currentHp)} / {character.hp}</span>
                    </div>
                  </div>
                  {character.mp > 0 && (
                    <div className="cm-bar-container">
                      <span className="cm-bar-label">MP</span>
                      <div className="cm-bar">
                        <div className="cm-bar-fill mp" style={{ width: `${charMpPct}%` }} />
                        <span className="cm-bar-text">{character.currentMp} / {character.mp}</span>
                      </div>
                    </div>
                  )}
                  <div className="cm-char-combat-stats">
                    <span>ATT: {character.att}</span>
                    <span>DEF: {character.def}</span>
                    <span>DR: {character.dr || 0}</span>
                  </div>
                  {renderEffects(character, 'You')}
                  {character.missChance > 0 && <div className="cm-indicator">Miss chance: {character.missChance}%</div>}
                  {character.mirrorImages > 0 && <div className="cm-indicator">Mirror images: {character.mirrorImages}</div>}

                  {/* Pet */}
                  {pet && pet.currentHp > 0 && (
                    <div className="cm-pet">
                      <div className="cm-floats">
                        {floatingNumbers.filter(f => f.target === 'pet').map(f => (
                          <div key={f.id} className={`floating-number floating-${f.type}`}>
                            {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
                          </div>
                        ))}
                      </div>
                      <div className="cm-pet-name">{pet.name} Lv.{pet.level}</div>
                      <div className="cm-bar-container">
                        <span className="cm-bar-label">HP</span>
                        <div className="cm-bar">
                          <div className="cm-bar-fill pet" style={{ width: `${petHpPct}%` }} />
                          <span className="cm-bar-text">{pet.currentHp} / {pet.hp}</span>
                        </div>
                      </div>
                      <div className="cm-pet-stats">
                        <span>ATT: {pet.att}</span>
                        <span>DEF: {pet.def}</span>
                      </div>
                    </div>
                  )}

                  {/* AP diamonds (deck mode) */}
                  {deckState && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                      <div className="cm-ap-row">
                        {Array.from({ length: deckState.maxAp + (deckState.bonusAp || 0) }).map((_, i) => {
                          const isBonusPip = i >= deckState.maxAp;
                          const isFilled = i < deckState.ap;
                          return <div key={i} className={`cm-ap-pip ${isFilled ? (isBonusPip ? 'bonus' : 'filled') : 'empty'}`} />;
                        })}
                      </div>
                      {deckState.block > 0 && (
                        <span className="cm-block-badge">🛡️ {deckState.block}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Card area — center */}
              <div className="cm-cards-area">
                {cardPlayError && <div className="cm-card-error">⚠️ {cardPlayError}</div>}

                {/* Deck mode */}
                {battle.deck && actionMode === 'main' && (
                  <>
                    <CardHand
                      deckState={battle.deck}
                      character={character}
                      characterMp={character.currentMp}
                      onPlayCard={handlePlayCard}
                      onEndTurn={handleEndTurn}
                      onScryResolve={handleScryResolve}
                      onExhaustResolve={handleExhaustResolve}
                      onDeckExhaustResolve={handleDeckExhaustResolve}
                      isPlayerTurn={isPlayerTurn}
                      pendingCardId={pendingCardId}
                      onTrack={track}
                      redrawMode={!!redrawState}
                      redrawSelectedIds={redrawState?.ids}
                      onRedrawToggle={handleRedrawToggle}
                      onRedrawConfirm={handleRedrawConfirm}
                      onRedrawCancel={handleRedrawCancel}
                      modernLayout
                    />
                    <div className="cm-utility-row">
                      {battle.deck && !battle.deck.baselineScryUsed && (
                        <button
                          className="cm-util-btn"
                          onClick={handleBaselineScry}
                          disabled={!isPlayerTurn || !!battle.deck.scryPending || (battle.deck.drawPileCount || 0) === 0}
                          title={`Peek at the top ${battle.deck.baselineScryCount || 2} cards and reorder. Once per combat.`}
                        >
                          🔮 Foresight ({battle.deck.baselineScryCount || 2})
                        </button>
                      )}
                      {battleItems.length > 0 && (
                        <button className="cm-util-btn" onClick={() => setActionMode('items')} disabled={!isPlayerTurn}>
                          🎒 Items ({battleItems.length})
                        </button>
                      )}
                      {(() => {
                        const equipped = (battle.learnedAbilities || []).filter(a => a.equipped);
                        if (equipped.length === 0) return null;
                        return (
                          <button className="cm-util-btn" onClick={() => setActionMode('mindlink')} disabled={!isPlayerTurn}>
                            🧠 Mind Link ({equipped.length})
                          </button>
                        );
                      })()}
                    </div>
                  </>
                )}

                {/* Classic mode */}
                {!battle.deck && actionMode === 'main' && (
                  <div className="cm-classic-actions">
                    <button className="action-btn attack-btn" onClick={handleAttack} disabled={!isPlayerTurn}>⚔️ Attack</button>
                    {battle.availableSpells?.length > 0 && (
                      <button className="action-btn spell-btn" onClick={() => setActionMode('spells')} disabled={!isPlayerTurn}>🔮 Spells ({battle.availableSpells.length})</button>
                    )}
                    {battle.availableAbilities?.length > 0 && (
                      <button className="action-btn ability-btn" onClick={() => setActionMode('abilities')} disabled={!isPlayerTurn}>💊 Abilities ({battle.availableAbilities.length})</button>
                    )}
                    {battleItems.length > 0 && (
                      <button className="action-btn item-btn" onClick={() => setActionMode('items')} disabled={!isPlayerTurn}>🎒 Items ({battleItems.length})</button>
                    )}
                    {(() => {
                      const equipped = (battle.learnedAbilities || []).filter(a => a.equipped);
                      if (equipped.length === 0) return null;
                      return <button className="action-btn ability-btn" onClick={() => setActionMode('mindlink')} disabled={!isPlayerTurn} style={{ borderColor: '#60a5fa' }}>🧠 Mind Link ({equipped.length})</button>;
                    })()}
                    {lastAction && (
                      <button className="action-btn repeat-btn" onClick={() => { if (canRepeatLastAction()) { const { label, ...act } = lastAction; safeAction(act); } }} disabled={!isPlayerTurn || !canRepeatLastAction()}>🔄 {lastAction.label}</button>
                    )}
                  </div>
                )}
              </div>

              {/* Right side — End Turn + deck info */}
              <div className="cm-right-actions">
                {battle.deck && actionMode === 'main' && (
                  <>
                    <button className="cm-end-turn-btn" onClick={handleEndTurn} disabled={!isPlayerTurn}>
                      End Turn
                    </button>
                    <div className="cm-deck-info">
                      <span>🃏 {deckState?.drawPile?.length || 0}</span>
                      <span>♻️ {deckState?.discardPile?.length || 0}</span>
                      <span>🚫 {deckState?.exhaustPile?.length || 0}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Sub-panels for spell/ability/item/mindlink selection */}
          {actionMode === 'spells' && (
            <div className="cm-sub-panel">
              <div className="spell-panel">
                <div className="spell-panel-header">
                  <h4>Select a Spell</h4>
                  <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back</button>
                </div>
                <div className="spell-filter-tabs">
                  <button className={`filter-tab ${spellFilter === 'all' ? 'active' : ''}`} onClick={() => setSpellFilter('all')}>All ({battle.availableSpells.length})</button>
                  {spellTypes.map(type => (
                    <button key={type} className={`filter-tab ${spellFilter === type ? 'active' : ''}`} onClick={() => setSpellFilter(type)} style={{ borderColor: SPELL_TYPE_INFO[type]?.color || '#666' }}>
                      {SPELL_TYPE_INFO[type]?.icon || ''} {SPELL_TYPE_INFO[type]?.label || type} ({groupedSpells[type].length})
                    </button>
                  ))}
                </div>
                <div className="spell-list">
                  {sortedFilteredSpells.map((spell, index) => {
                    const typeInfo = SPELL_TYPE_INFO[spell.type] || { label: spell.type, color: '#666', icon: '✨' };
                    const affordable = canAffordSpell(spell);
                    const isSelected = selectedSpell?.name === spell.name;
                    const isRecent = recentSpells.includes(spell.name);
                    // v640.6: precomputed damage/heal preview for this character
                    const preview = getSpellPreview(spell, character);
                    const previewTitle = getSpellTooltip(spell, character);
                    const resistBadge = getResistBadge(spell);
                    return (
                      <div key={spell.name} className={`spell-item ${isSelected ? 'selected' : ''} ${!affordable ? 'unaffordable' : ''} ${isRecent ? 'recently-used' : ''}`}
                        onClick={() => affordable && setSelectedSpell(spell)}
                        onDoubleClick={() => affordable && handleCastSpell(spell)}
                        title={previewTitle ? `${spell.name}\n${previewTitle}` : spell.name}>
                        {index < 9 && <span className="item-number">{index + 1}</span>}
                        <div className="spell-item-main">
                          <span className="spell-icon">{typeInfo.icon}</span>
                          <span className="spell-name">{spell.name}</span>
                          {isRecent && <span className="recent-badge">↻</span>}
                          {spell.element && ELEMENT_INFO[spell.element] && (
                            <span className="spell-element-badge" style={{ color: ELEMENT_INFO[spell.element].color }}>{ELEMENT_INFO[spell.element].icon} {spell.element}</span>
                          )}
                          <span className="spell-type-badge" style={{ background: typeInfo.color }}>{typeInfo.label}</span>
                          {preview && (
                            <span className="spell-preview-inline" style={{ marginLeft: 6, fontSize: '0.82em', color: preview.verb === 'heal' ? '#88dd88' : '#ffb070' }}>
                              {preview.min === preview.max ? preview.min : `${preview.min}–${preview.max}`} {preview.verb}
                            </span>
                          )}
                          {resistBadge && (
                            <span
                              title={resistBadge.title}
                              style={{ marginLeft: 6, fontSize: '0.7em', fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: resistBadge.color, background: resistBadge.bg, letterSpacing: 0.3 }}
                            >
                              {resistBadge.text}
                            </span>
                          )}
                        </div>
                        <div className="spell-item-cost"><span className={`mp-cost ${!affordable ? 'too-expensive' : ''}`}>{spell.mp} MP</span></div>
                      </div>
                    );
                  })}
                </div>
                {selectedSpell && (
                  <div className="spell-cast-bar">
                    <div className="selected-spell-info">
                      <strong>{(SPELL_TYPE_INFO[selectedSpell.type]?.icon || '')} {selectedSpell.name}</strong>
                      <span className="spell-detail-cost"> — {selectedSpell.mp} MP</span>
                    </div>
                    <button className="cast-btn" onClick={() => handleCastSpell(selectedSpell)} disabled={!canAffordSpell(selectedSpell)}>Cast {selectedSpell.name}!</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {actionMode === 'abilities' && (
            <div className="cm-sub-panel">
              <div className="ability-panel">
                <div className="ability-panel-header">
                  <h4>Select an Ability</h4>
                  <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back</button>
                </div>
                <div className="ability-list">
                  {battle.availableAbilities.filter(a => !a.fellowshipOnly).map((ability, index) => {
                    const isSelected = selectedAbility?.name === ability.name;
                    const mpCost = ability.mp || 0;
                    const affordable = character.currentMp >= mpCost || mpCost === 0;
                    const isCoolingDown = ability.onCooldown;
                    const usable = affordable && !isCoolingDown;
                    return (
                      <div key={ability.name || `ability_${index}`} className={`ability-item ${isSelected ? 'selected' : ''} ${!usable ? 'unaffordable' : ''}`}
                        onClick={() => usable && setSelectedAbility(ability)}
                        onDoubleClick={() => usable && handleUseAbility(ability)}>
                        {index < 9 && <span className="item-number">{index + 1}</span>}
                        <div className="ability-item-main">
                          <span className="ability-name">{ability.type === 'skill' && <span className="skill-tag">🌳</span>}{ability.name}</span>
                          {isCoolingDown ? <span className="ability-desc" style={{color:'#ff6b6b'}}>⏳ Cooldown: {Math.ceil(ability.cooldownRemaining/60)} min</span>
                            : ability.description && <span className="ability-desc">{ability.description}</span>}
                          {/* v640.6: scaling preview for known damage abilities */}
                          {(() => {
                            const ap = getAbilityPreview(ability, character);
                            return ap ? <span className="ability-desc" style={{ color: '#ffb070', fontSize: '0.85em' }}>📈 {ap.text}</span> : null;
                          })()}
                        </div>
                        <div className="ability-item-cost">{mpCost > 0 ? <span className={`mp-cost ${!affordable ? 'too-expensive' : ''}`}>{mpCost} MP</span> : <span className="mp-cost free">Free</span>}</div>
                      </div>
                    );
                  })}
                </div>
                {selectedAbility && (
                  <div className="ability-use-bar">
                    <div className="selected-ability-info"><strong>{selectedAbility.name}</strong>{selectedAbility.description && ` — ${selectedAbility.description}`}</div>
                    <button className="use-ability-btn" onClick={() => handleUseAbility(selectedAbility)}>Use {selectedAbility.name}!</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {actionMode === 'items' && (
            <div className="cm-sub-panel">
              <div className="ability-panel">
                <div className="ability-panel-header">
                  <h4>💊 Use an Item</h4>
                  <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back</button>
                </div>
                {battle?.deck && (
                  <div className="item-ap-hint">Items cost AP based on their strength — you keep your turn.</div>
                )}
                <div className="ability-list">
                  {battleItems.map((item, index) => {
                    const isHP = item.name?.toLowerCase().includes('health') || (item.hp && item.hp > 0);
                    const isMP = item.name?.toLowerCase().includes('mana') || (item.mp && item.mp > 0 && !item.hp);
                    const typeIcon = isHP ? '❤️' : isMP ? '💙' : item.type === 'scroll' ? '📜' : '🧪';
                    const isSelected = selectedItem?.name === item.name;
                    const apCost = battle?.deck ? (item.apCost || 1) : 0;
                    const unaffordable = apCost > 0 && (battle?.deck?.ap ?? 0) < apCost;
                    return (
                      <div key={`${item.name}_${item.type||''}_${index}`}
                        className={`ability-item ${isSelected ? 'selected' : ''} ${unaffordable ? 'unaffordable' : ''}`}
                        onClick={() => !unaffordable && setSelectedItem(item)}
                        onDoubleClick={() => !unaffordable && handleUseItem(item)}>
                        {index < 9 && <span className="item-number">{index + 1}</span>}
                        <div className="ability-item-main">
                          <span className="ability-name">{typeIcon} {item.name}</span>
                          <span className="ability-desc">{item.hp ? `+${item.hp} HP ` : ''}{item.mp ? `+${item.mp} MP ` : ''}{item.type === 'scroll' ? 'Scroll spell' : ''}</span>
                        </div>
                        <div className="ability-item-cost">
                          {apCost > 0 && <span className="item-ap-cost">{apCost} AP</span>}
                          <span className="mp-cost free">{item.quantity || 1}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {selectedItem && (
                  <div className="ability-use-bar">
                    <div className="selected-ability-info">
                      <strong>{selectedItem.name}</strong> — {selectedItem.quantity || 1} remaining
                      {battle?.deck && ` · ${selectedItem.apCost || 1} AP`}
                    </div>
                    <button className="use-ability-btn" onClick={() => handleUseItem(selectedItem)} style={{ background: '#27ae60' }}>Use {selectedItem.name}!</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {actionMode === 'mindlink' && (
            <div className="cm-sub-panel">
              <div className="ability-panel">
                <div className="ability-panel-header">
                  <h4>🧠 Mind Link Abilities</h4>
                  <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back</button>
                </div>
                <div className="ability-list">
                  {(battle.learnedAbilities || []).filter(a => a.equipped).map((la) => {
                    const mpCost = Math.max(1, Math.floor((battle.character?.mp || 100) * 0.10));
                    return (
                      <div key={la.key} className="ability-item" onClick={() => { safeAction({ type: 'learned_ability', abilityKey: la.key }); setActionMode('main'); }} style={{ cursor: 'pointer' }}>
                        <div className="ability-item-info">
                          <span className="ability-item-name" style={{ color: '#60a5fa' }}>🧠 {la.name}</span>
                          <span className="ability-item-desc">{la.damage} {la.element || la.type || ''} dmg • from {la.source}</span>
                        </div>
                        <div className="ability-item-cost"><span className="mp-cost">{mpCost} MP</span></div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  // ════════════════════════════════════════════════════════════
  //  END MODERN LAYOUT — Classic layout follows below
  // ════════════════════════════════════════════════════════════

  return (
    <div className={`combat-interface ${isLowHp ? 'low-hp' : ''} ${isDangerHp ? 'danger-hp' : ''}`}>
      <audio ref={battleAudioRef} loop />

      {/* Turn transition overlay */}
      {turnTransition && (
        <div className={`turn-transition turn-${turnTransition}`}>
          <span className="turn-transition-text">
            {turnTransition === 'player' ? '✨ Your Turn!' : `${monster.name}'s Turn`}
          </span>
        </div>
      )}

      {/* Low HP danger vignette */}
      {isLowHp && <div className={`danger-vignette ${isDangerHp ? 'critical' : ''}`} />}

      {/* Combat Tutorial (first fight) */}
      {showCombatTutorial && <CombatTutorial onComplete={() => setShowCombatTutorial(false)} characterId={characterId} />}
      <div className="combat-header">
        <div className="combat-header-row">
          <h2>⚔️ Battle  Round {battle.round}</h2>
          {battleMusic && (
            <button
              className={`combat-music-btn ${isMusicPlaying ? 'playing' : ''}`}
              onClick={toggleBattleMusic}
              title={isMusicPlaying ? 'Pause battle music' : 'Play battle music'}
            >
              {isMusicPlaying ? '🔊' : '🔇'}
            </button>
          )}
        </div>
        <div className="battle-status">{getStatusMessage()}</div>
      </div>

      <div className="combat-main">
        {/* Monster Section */}
        <div className="monster-section">
          <div className="monster-display">
            {/* Floating damage numbers */}
            {floatingNumbers.filter(f => f.target === 'monster').map(f => (
              <div key={f.id} className={`floating-number floating-${f.type}`}>
                {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
              </div>
            ))}
            <img
              src={`/images/monsters/${monster.pic}`}
              alt={monster.name}
              className={`monster-image${monster.eliteTier ? ` elite-tier-${monster.eliteTier}` : ''}`}
              onError={(e) => { e.target.src = '/images/monsters/default.jpg'; }}
            />
            <div className="monster-info">
              <h3>{monster.name}</h3>
              <span className="entity-level">Level {monster.level}</span>
              {monster.eliteTier && (
                <span className={`monster-elite-badge elite-badge-${monster.eliteTier}`}>
                  {monster.eliteTier === 'legendary' ? '👑 LEGENDARY' : monster.eliteTier === 'champion' ? '💀 CHAMPION' : '⚠️ ELITE'}
                </span>
              )}
              {monster.type && (
                <span className="monster-type-badge">{monster.type}</span>
              )}
              <div className="health-bar-container">
                <div className="health-label">HP</div>
                <div className="health-bar">
                  <div className="health-fill monster-health" style={{ width: `${monHpPct}%`, background: getHpBarGradient(monHpPct) }} />
                  <span className="bar-overlay-text">{monster.currentHp} / {monster.hp}</span>
                </div>
              </div>
              {renderEffects(monster, 'Monster')}
              {/* v403: Boss Mechanic Indicator */}
              {monster._bossMechanic && (() => {
                const bm = monster._bossMechanic;
                const mechType = bm.type;
                let icon = '⚙️';
                let label = '';
                let detail = '';
                if (mechType === 'shell') {
                  icon = bm._shellActive ? '🛡️' : '💥';
                  label = bm._shellActive ? 'Armored Shell' : 'Shell Broken!';
                  detail = bm._shellActive
                    ? `DR +${bm.shellDR} — Deal ${bm.breakThreshold}+ in one hit to shatter!`
                    : `Vulnerable! ×${bm.vulnerabilityDamageMult || 1.5} damage for ${bm._vulnerableTurns}t`;
                } else if (mechType === 'escalate') {
                  icon = '📈';
                  label = `${bm.stackBuffName || 'Escalation'} x${bm._stacks || 0}`;
                  detail = `DEF +${bm._totalDef || 0} / DR +${bm._totalDR || 0} (max ${bm.maxStacks || 8})`;
                } else if (mechType === 'charge_up') {
                  icon = '⚡';
                  label = `Charging: ${bm._stacks || 0} / ${bm.maxStacks || 3}`;
                  detail = bm._stacks >= (bm.maxStacks || 3) - 1
                    ? '⚠️ NEXT TURN: Devastating attack!'
                    : 'Building power...';
                } else if (mechType === 'crushing_weight') {
                  icon = '🏋️';
                  label = `Weight: ${bm._stacks || 0} / ${bm.crushThreshold || 5}`;
                  detail = bm._stacks >= (bm.crushThreshold || 5) - 1
                    ? '⚠️ NEXT TURN: Crushing blow!'
                    : `DEF +${bm._totalDef || 0} / DR +${bm._totalDR || 0}`;
                } else if (mechType === 'multi_action') {
                  icon = '⚔️';
                  label = 'Multi-Action';
                  detail = 'Attacks twice per turn!';
                }
                return (
                  <div className={`boss-mechanic-indicator mechanic-${mechType}${
                    (mechType === 'shell' && !bm._shellActive) ? ' vulnerable' : ''
                  }${(mechType === 'charge_up' && bm._stacks >= (bm.maxStacks || 3) - 1) ? ' warning' : ''
                  }${(mechType === 'crushing_weight' && bm._stacks >= (bm.crushThreshold || 5) - 1) ? ' warning' : ''}`}>
                    <span className="mechanic-icon">{icon}</span>
                    <span className="mechanic-label">{label}</span>
                    <span className="mechanic-detail">{detail}</span>
                  </div>
                );
              })()}
              {monster.missChance > 0 && (
                <div className="miss-chance-indicator">Miss chance: {monster.missChance}%</div>
              )}
              {monster.mirrorImages > 0 && (
                <div className="mirror-image-indicator">Mirror images: {monster.mirrorImages}</div>
              )}
            </div>
          </div>
        </div>

        {/* Battle Log */}
        <div className="battle-log">
          <h4>⚔️ Battle Log</h4>
          <div className="log-messages" ref={logRef}>
            {battle.log.map((message, index) => (
              <div key={index} className={`log-message ${classifyLogMessage(message)} ${index === battle.log.length - 1 ? 'latest' : ''}`}>
                {message}
              </div>
            ))}
          </div>
        </div>

        {/* Character Section */}
        <div className="character-section">
          <div className="character-info">
            {/* Floating damage/heal numbers */}
            {floatingNumbers.filter(f => f.target === 'player').map(f => (
              <div key={f.id} className={`floating-number floating-${f.type}`}>
                {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
              </div>
            ))}
            <h3>{character.name}</h3>
            <span className="entity-level">{character.advanced_class || character.class}  Level {character.level}</span>

            <div className="health-bar-container">
              <div className="health-label">HP</div>
              <div className={`health-bar ${isLowHp ? 'bar-danger' : ''}`}>
                <div className="health-fill character-health" style={{ width: `${charHpPct}%`, background: getHpBarGradient(charHpPct) }} />
                <span className="bar-overlay-text">{Math.max(0, character.currentHp)} / {character.hp}</span>
              </div>
            </div>

            {character.mp > 0 && (
              <div className="mana-bar-container">
                <div className="mana-label">MP</div>
                <div className="mana-bar">
                  <div className="mana-fill" style={{ width: `${charMpPct}%` }} />
                  <span className="bar-overlay-text">{character.currentMp} / {character.mp}</span>
                </div>
              </div>
            )}

            <div className="combat-stats-row">
              <span className="combat-stat">ATT: {character.att}</span>
              <span className="combat-stat">DEF: {character.def}</span>
              <span className="combat-stat">DR: {character.dr || 0}</span>
            </div>

            {renderEffects(character, 'You')}
            {character.missChance > 0 && (
              <div className="miss-chance-indicator">Miss chance: {character.missChance}%</div>
            )}
            {character.mirrorImages > 0 && (
              <div className="mirror-image-indicator">Mirror images: {character.mirrorImages}</div>
            )}
          </div>

          {/* Pet Section — under player info */}
          {pet && pet.currentHp > 0 && (
            <div className="pet-info">
              {/* Floating damage numbers for pet */}
              {floatingNumbers.filter(f => f.target === 'pet').map(f => (
                <div key={f.id} className={`floating-number floating-${f.type}`}>
                  {f.type === 'damage' ? `-${f.value}` : `+${f.value}`}
                </div>
              ))}
              <h4>{pet.name} <span className="entity-level">Lv.{pet.level}</span></h4>
              <div className="health-bar-container">
                <div className="health-label">HP</div>
                <div className="health-bar">
                  <div className="health-fill pet-health" style={{ width: `${petHpPct}%` }} />
                  <span className="bar-overlay-text">{pet.currentHp} / {pet.hp}</span>
                </div>
              </div>
              <div className="combat-stats-row">
                <span className="combat-stat">ATT: {pet.att}</span>
                <span className="combat-stat">DEF: {pet.def}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Panel */}
      {/* AC Status Indicators */}
      {battle.status === 'active' && (battle.activeMetamagic || battle.spiritDanceActive || battle.mindBladeActive || 
       battle.wildShapeActive || battle.doomActive || battle.retributionActive || battle.criticalAssaultActive || 
       battle.divineWrathActive || battle.soulBreakerActive || battle.naturesWrathActive || battle.arcanePsionicsActive ||
       battle.magnifySpellActive || battle.convergenceActive || battle.imbueMode || 
       (battle.imbueEffects && battle.imbueEffects.length > 0)) && (
        <div className="ac-status-bar">
          {battle.activeMetamagic && (
            <span className="ac-badge ac-badge-metamagic">
              ✨ Metamagic: {battle.metamagicOptions?.[battle.activeMetamagic]?.name || battle.activeMetamagic}
            </span>
          )}
          {battle.spiritDanceActive && (
            <span className="ac-badge ac-badge-dance">
              💃 Dancing: {battle.spiritDanceType}
            </span>
          )}
          {battle.mindBladeActive && (
            <span className="ac-badge ac-badge-mind">
              🧠 Mind Blade ({battle.mindBladeRounds}r)
            </span>
          )}
          {battle.wildShapeActive && (
            <span className="ac-badge ac-badge-nature">
              🐺 Wild Shape ({battle.wildShapeRounds}r)
            </span>
          )}
          {battle.doomActive && (
            <span className="ac-badge ac-badge-doom">
              ☠️ Doom ({battle.doomRounds}r)
            </span>
          )}
          {battle.retributionActive && (
            <span className="ac-badge ac-badge-retribution">
              ⚔️ Retribution ({battle.retributionRounds}r)
            </span>
          )}
          {battle.criticalAssaultActive && (
            <span className="ac-badge ac-badge-critical">
              💀 Critical Assault ({battle.criticalAssaultRounds}r)
            </span>
          )}
          {battle.divineWrathActive && (
            <span className="ac-badge ac-badge-divine">
              🛡️ Divine Wrath ({battle.divineWrathRounds}r)
            </span>
          )}
          {battle.soulBreakerActive && (
            <span className="ac-badge ac-badge-soul">
              💥 Soul Breaker ({battle.soulBreakerRounds}r)
            </span>
          )}
          {battle.naturesWrathActive && (
            <span className="ac-badge ac-badge-natureswrath">
              🌿 Nature's Wrath ({battle.naturesWrathRounds}r)
            </span>
          )}
          {battle.arcanePsionicsActive && (
            <span className="ac-badge ac-badge-arcane">
              ⚡ Arcane Psionics ({battle.arcanePsionicsRounds}r)
            </span>
          )}
          {battle.magnifySpellActive && (
            <span className="ac-badge ac-badge-doom">
              ⚡ Magnify Spell (next)
            </span>
          )}
          {battle.convergenceActive && (
            <span className="ac-badge ac-badge-divine">
              ✨ {battle.convergenceType === 'arcane_prayer' ? 'Arcane Prayer' : 'Theurgic Convergence'} (next spell)
            </span>
          )}
          {battle.imbueMode && (
            <span className="ac-badge ac-badge-imbue">
              ⚔️🔮 Imbue Mode — cast a damage spell!
            </span>
          )}
          {battle.imbueEffects && battle.imbueEffects.map((imbue, idx) => (
            <span key={`imbue-${idx}`} className="ac-badge ac-badge-retribution">
              ⚔️🔮 {imbue.spellName} ({imbue.element}) — {imbue.rounds}r
            </span>
          ))}
        </div>
      )}

      {/* Metamagic Toggle Buttons */}
      {battle.status === 'active' && battle.metamagicOptions && Object.keys(battle.metamagicOptions).length > 0 && isPlayerTurn && (
        <div className="metamagic-toggles">
          <span className="metamagic-label">Metamagic:</span>
          {Object.entries(battle.metamagicOptions).map(([key, mm]) => (
            <button
              key={key}
              className={`metamagic-btn${battle.activeMetamagic === key ? ' active' : ''}`}
              onClick={() => onAction({ type: 'metamagic_toggle', metamagicKey: key })}
              title={`${mm.name || key}: ×${mm.mpMultiplier || 1} MP cost`}
            >
              {battle.activeMetamagic === key ? '✨ ' : ''}{mm.name || key} (×{mm.mpMultiplier || 1} MP)
            </button>
          ))}
        </div>
      )}

      {/* ── Class System Gauge (Momentum, Marks, Spell Slots, Aspects, Focus, Bond) ── */}
      {battle.status === 'active' && battle.classSystemState && (
        <ClassSystemGauge
          classSystemState={battle.classSystemState}
          onAction={onAction}
          isPlayerTurn={isPlayerTurn}
        />
      )}

      {/* Safety close button when battle has ended but modal is still showing */}
      {battle.status !== 'active' && (
        <div className="action-panel" style={{ textAlign: 'center', padding: '1rem' }}>
          <p style={{ color: '#ccc', marginBottom: '0.75rem', fontSize: '0.9rem' }}>{getStatusMessage()}</p>
          <button
            className="action-btn attack-btn"
            onClick={onClose}
            style={{ maxWidth: '200px', margin: '0 auto' }}
          >
            Continue
          </button>
        </div>
      )}

      {battle.status === 'active' && (
        <div className="action-panel">
          {/* Main action buttons */}
          {/* ── DECK MODE: Card Hand replaces classic action buttons ── */}
          {battle.deck && actionMode === 'main' && (
            <div className="deck-combat-panel">
              <CardHand
                deckState={battle.deck}
                character={character}
                characterMp={character.currentMp}
                onPlayCard={handlePlayCard}
                onEndTurn={handleEndTurn}
                onScryResolve={handleScryResolve}
                onExhaustResolve={handleExhaustResolve}
                onDeckExhaustResolve={handleDeckExhaustResolve}
                isPlayerTurn={isPlayerTurn}
                pendingCardId={pendingCardId}
                onTrack={track}
                redrawMode={!!redrawState}
                redrawSelectedIds={redrawState?.ids}
                onRedrawToggle={handleRedrawToggle}
                onRedrawConfirm={handleRedrawConfirm}
                onRedrawCancel={handleRedrawCancel}
              />
              {cardPlayError && (
                <div className="card-play-error" style={{
                  background: 'rgba(231,76,60,0.9)', color: '#fff', padding: '6px 16px',
                  borderRadius: '6px', textAlign: 'center', fontSize: '0.9em', fontWeight: 'bold',
                  animation: 'fadeInOut 2.5s ease-in-out',
                  position: 'absolute', top: '-32px', left: '50%', transform: 'translateX(-50%)',
                  whiteSpace: 'nowrap', zIndex: 100, pointerEvents: 'none',
                }}>
                  ⚠️ {cardPlayError}
                </div>
              )}
              <div className="deck-utility-actions">
                {battle.deck && !battle.deck.baselineScryUsed && (
                  <button
                    className="action-btn"
                    onClick={handleBaselineScry}
                    disabled={!isPlayerTurn || !!battle.deck.scryPending || (battle.deck.drawPileCount || 0) === 0}
                    title={`Peek at the top ${battle.deck.baselineScryCount || 2} cards and reorder. Once per combat.`}
                    style={{ borderColor: '#a78bfa' }}
                  >
                    🔮 Foresight ({battle.deck.baselineScryCount || 2})
                  </button>
                )}
                {battleItems.length > 0 && (
                  <button
                    className="action-btn item-btn"
                    onClick={() => setActionMode('items')}
                    disabled={!isPlayerTurn}
                  >
                    🎒 Items ({battleItems.length}) <span className="key-hint">[I]</span>
                  </button>
                )}
                {!battle.noRun && (
                  <button
                    className="action-btn run-btn"
                    onClick={handleRun}
                    disabled={!isPlayerTurn}
                  >
                    🏃 Run <span className="key-hint">[R]</span>
                  </button>
                )}
                {(() => {
                  const equipped = (battle.learnedAbilities || []).filter(a => a.equipped);
                  if (equipped.length === 0) return null;
                  return (
                    <button
                      className="action-btn ability-btn"
                      onClick={() => setActionMode('mindlink')}
                      disabled={!isPlayerTurn}
                      style={{ borderColor: '#60a5fa' }}
                    >
                      🧠 Mind Link ({equipped.length})
                    </button>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ── CLASSIC MODE: Original action buttons (when no deck system) ── */}
          {!battle.deck && actionMode === 'main' && (
            <div className="main-actions">
              <button
                className="action-btn attack-btn"
                onClick={handleAttack}
                disabled={!isPlayerTurn}
              >
                 Attack <span className="key-hint">[A]</span>
              </button>

              {battle.availableSpells && battle.availableSpells.length > 0 && (
                <button
                  className="action-btn spell-btn"
                  onClick={() => setActionMode('spells')}
                  disabled={!isPlayerTurn}
                >
                  🔮 Cast Spell ({battle.availableSpells.length}) <span className="key-hint">[S]</span>
                </button>
              )}

              {battle.availableAbilities && battle.availableAbilities.length > 0 && (
                <button
                  className="action-btn ability-btn"
                  onClick={() => setActionMode('abilities')}
                  disabled={!isPlayerTurn}
                >
                  💊 Use Ability ({battle.availableAbilities.length}) <span className="key-hint">[D]</span>
                </button>
              )}

              {battleItems.length > 0 && (
                <button
                  className="action-btn item-btn"
                  onClick={() => setActionMode('items')}
                  disabled={!isPlayerTurn}
                >
                  💊 Use Item ({battleItems.length}) <span className="key-hint">[I]</span>
                </button>
              )}

              {/* Mind Link: equipped learned abilities */}
              {(() => {
                const equipped = (battle.learnedAbilities || []).filter(a => a.equipped);
                if (equipped.length === 0) return null;
                return (
                  <button
                    className="action-btn ability-btn"
                    onClick={() => setActionMode('mindlink')}
                    disabled={!isPlayerTurn}
                    style={{ borderColor: '#60a5fa' }}
                  >
                    🧠 Mind Link ({equipped.length})
                  </button>
                );
              })()}

              {!battle.noRun && (
                <button
                  className="action-btn run-btn"
                  onClick={handleRun}
                  disabled={!isPlayerTurn}
                >
                   Run Away <span className="key-hint">[R]</span>
                </button>
              )}

              {lastAction && (
                <button
                  className="action-btn repeat-btn"
                  onClick={() => { if (canRepeatLastAction()) { const { label, ...act } = lastAction; safeAction(act); } }}
                  disabled={!isPlayerTurn || !canRepeatLastAction()}
                >
                   Repeat: {lastAction.label} <span className="key-hint">[Q]</span>
                </button>
              )}
            </div>
          )}

          {/* Spell Selection */}
          {actionMode === 'spells' && (
            <div className="spell-panel">
              <div className="spell-panel-header">
                <h4>Select a Spell <span className="panel-hint">1-9 to cast  Esc to back</span></h4>
                <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back <span className="key-hint">[Esc]</span></button>
              </div>

              {/* Spell type filter tabs */}
              <div className="spell-filter-tabs">
                <button
                  className={`filter-tab ${spellFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setSpellFilter('all')}
                >
                  All ({battle.availableSpells.length})
                </button>
                {spellTypes.map(type => (
                  <button
                    key={type}
                    className={`filter-tab ${spellFilter === type ? 'active' : ''}`}
                    onClick={() => setSpellFilter(type)}
                    style={{
                      borderColor: SPELL_TYPE_INFO[type]?.color || '#666'
                    }}
                  >
                    {SPELL_TYPE_INFO[type]?.icon || ''} {SPELL_TYPE_INFO[type]?.label || type} ({groupedSpells[type].length})
                  </button>
                ))}
              </div>

              {/* Spell list */}
              <div className="spell-list">
                {sortedFilteredSpells.map((spell, index) => {
                  const typeInfo = SPELL_TYPE_INFO[spell.type] || { label: spell.type, color: '#666', icon: '✨' };
                  const affordable = canAffordSpell(spell);
                  const isSelected = selectedSpell?.name === spell.name;
                  const isRecent = recentSpells.includes(spell.name);
                  const seedInfo = spell.prereqs && Object.keys(spell.prereqs).length > 0
                    ? '\nSeeds: ' + Object.entries(spell.prereqs).map(([s,l]) => `${s} Lv${l}`).join(', ')
                    : '';
                  // v640.6: damage/heal preview for this character
                  const preview = getSpellPreview(spell, character);
                  const previewTitle = getSpellTooltip(spell, character);
                  const resistBadge = getResistBadge(spell);
                  return (
                    <div
                      key={spell.name}
                      className={`spell-item ${isSelected ? 'selected' : ''} ${!affordable ? 'unaffordable' : ''} ${isRecent ? 'recently-used' : ''}`}
                      onClick={() => affordable && setSelectedSpell(spell)}
                      onDoubleClick={() => affordable && handleCastSpell(spell)}
                      title={`${spell.name}\nType: ${typeInfo.label}\nCost: ${spell.mp} MP${previewTitle ? '\n' + previewTitle : ''}${seedInfo}`}
                    >
                      {index < 9 && <span className="item-number">{index + 1}</span>}
                      <div className="spell-item-main">
                        <span className="spell-icon">{typeInfo.icon}</span>
                        <span className="spell-name">{spell.name}</span>
                        {isRecent && <span className="recent-badge"></span>}
                        {spell.element && ELEMENT_INFO[spell.element] && (
                          <span className="spell-element-badge" style={{ color: ELEMENT_INFO[spell.element].color }}>
                            {ELEMENT_INFO[spell.element].icon} {spell.element}
                          </span>
                        )}
                        <span className="spell-type-badge" style={{ background: typeInfo.color }}>
                          {typeInfo.label}
                        </span>
                        {preview && (
                          <span className="spell-preview-inline" style={{ marginLeft: 6, fontSize: '0.82em', color: preview.verb === 'heal' ? '#88dd88' : '#ffb070' }}>
                            {preview.min === preview.max ? preview.min : `${preview.min}–${preview.max}`} {preview.verb}
                          </span>
                        )}
                        {resistBadge && (
                          <span
                            title={resistBadge.title}
                            style={{ marginLeft: 6, fontSize: '0.7em', fontWeight: 700, padding: '1px 5px', borderRadius: 3, color: resistBadge.color, background: resistBadge.bg, letterSpacing: 0.3 }}
                          >
                            {resistBadge.text}
                          </span>
                        )}
                      </div>
                      <div className="spell-item-cost">
                        <span className={`mp-cost ${!affordable ? 'too-expensive' : ''}`}>
                          {spell.mp} MP
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Cast button */}
              {selectedSpell && (
                <div className="spell-cast-bar">
                  <div className="selected-spell-info">
                    <strong>{(SPELL_TYPE_INFO[selectedSpell.type]?.icon || '')} {selectedSpell.name}</strong>
                    <span className="spell-detail-badge" style={{color: SPELL_TYPE_INFO[selectedSpell.type]?.color || '#666'}}>
                      {' '} {SPELL_TYPE_INFO[selectedSpell.type]?.label || selectedSpell.type}
                    </span>
                    <span className="spell-detail-cost">  {selectedSpell.mp} MP</span>
                    {selectedSpell.prereqs && Object.keys(selectedSpell.prereqs).length > 0 && (
                      <span className="spell-detail-seeds">  Seeds: {Object.entries(selectedSpell.prereqs).map(([s,l]) => `${s} Lv${l}`).join(', ')}</span>
                    )}
                  </div>
                  <button
                    className="cast-btn"
                    onClick={() => handleCastSpell(selectedSpell)}
                    disabled={!canAffordSpell(selectedSpell)}
                  >
                    Cast {selectedSpell.name}!
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Ability Selection */}
          {actionMode === 'abilities' && (
            <div className="ability-panel">
              <div className="ability-panel-header">
                <h4>Select an Ability <span className="panel-hint">1-9 to use  Esc to back</span></h4>
                <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back <span className="key-hint">[Esc]</span></button>
              </div>

              <div className="ability-list">
                {battle.availableAbilities.filter(a => !a.fellowshipOnly).map((ability, index) => {
                  const isSelected = selectedAbility?.name === ability.name;
                  const mpCost = ability.mp || 0;
                  const affordable = character.currentMp >= mpCost || mpCost === 0;
                  const isCoolingDown = ability.onCooldown;
                  const usable = affordable && !isCoolingDown;
                  return (
                    <div
                      key={ability.name || `ability_${index}`}
                      className={`ability-item ${isSelected ? 'selected' : ''} ${!usable ? 'unaffordable' : ''}`}
                      onClick={() => usable && setSelectedAbility(ability)}
                      onDoubleClick={() => usable && handleUseAbility(ability)}
                    >
                      {index < 9 && <span className="item-number">{index + 1}</span>}
                      <div className="ability-item-main">
                        <span className="ability-name">
                          {ability.type === 'skill' && <span className="skill-tag">🌳</span>}
                          {ability.name}
                        </span>
                        {isCoolingDown && (
                          <span className="ability-desc" style={{color: '#ff6b6b'}}>
                            ⏳ Cooldown: {Math.ceil(ability.cooldownRemaining / 60)} min
                          </span>
                        )}
                        {!isCoolingDown && ability.description && (
                          <span className="ability-desc">{ability.description}</span>
                        )}
                      </div>
                      <div className="ability-item-cost">
                        {mpCost > 0 && (
                          <span className={`mp-cost ${!affordable ? 'too-expensive' : ''}`}>
                            {mpCost} MP
                          </span>
                        )}
                        {mpCost === 0 && <span className="mp-cost free">Free</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedAbility && (
                <div className="ability-use-bar">
                  <div className="selected-ability-info">
                    <strong>{selectedAbility.name}</strong>
                    {selectedAbility.description && `  ${selectedAbility.description}`}
                  </div>
                  <button
                    className="use-ability-btn"
                    onClick={() => handleUseAbility(selectedAbility)}
                  >
                    Use {selectedAbility.name}!
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Item Selection */}
          {actionMode === 'items' && (
            <div className="ability-panel">
              <div className="ability-panel-header">
                <h4>💊 Use an Item <span className="panel-hint">1-9 to use  Esc to back</span></h4>
                <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back <span className="key-hint">[Esc]</span></button>
              </div>

              <div className="ability-list">
                {battleItems.map((item, index) => {
                  const isHP = item.name?.toLowerCase().includes('health') || (item.hp && item.hp > 0);
                  const isMP = item.name?.toLowerCase().includes('mana') || (item.mp && item.mp > 0 && !item.hp);
                  const typeIcon = isHP ? '' : isMP ? '' : item.type === 'scroll' ? '' : '';
                  const isSelected = selectedItem?.name === item.name;
                  const apCost = battle?.deck ? (item.apCost || 1) : 0;
                  const unaffordable = apCost > 0 && (battle?.deck?.ap ?? 0) < apCost;
                  return (
                    <div
                      key={`${item.name}_${item.type || ''}_${index}`}
                      className={`ability-item ${isSelected ? 'selected' : ''} ${unaffordable ? 'unaffordable' : ''}`}
                      onClick={() => !unaffordable && setSelectedItem(item)}
                      onDoubleClick={() => !unaffordable && handleUseItem(item)}
                    >
                      {index < 9 && <span className="item-number">{index + 1}</span>}
                      <div className="ability-item-main">
                        <span className="ability-name">{typeIcon} {item.name}</span>
                        <span className="ability-desc">
                          {item.hp ? `+${item.hp} HP ` : ''}
                          {item.mp ? `+${item.mp} MP ` : ''}
                          {item.type === 'scroll' ? `Scroll spell` : ''}
                        </span>
                      </div>
                      <div className="ability-item-cost">
                        {apCost > 0 && <span className="item-ap-cost">{apCost} AP</span>}
                        <span className="mp-cost free">{item.quantity || 1}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selectedItem && (
                <div className="ability-use-bar">
                  <div className="selected-ability-info">
                    <strong>{selectedItem.name}</strong>  {selectedItem.quantity || 1} remaining
                  </div>
                  <button
                    className="use-ability-btn"
                    onClick={() => handleUseItem(selectedItem)}
                    style={{ background: '#27ae60' }}
                  >
                    Use {selectedItem.name}!
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mind Link — Learned Ability Selection */}
          {actionMode === 'mindlink' && (
            <div className="ability-panel">
              <div className="ability-panel-header">
                <h4>🧠 Mind Link Abilities <span className="panel-hint">Click to use  Esc to back</span></h4>
                <button className="back-btn" onClick={() => setActionMode('main')}>⬅️ Back <span className="key-hint">[Esc]</span></button>
              </div>

              <div className="ability-list">
                {(battle.learnedAbilities || []).filter(a => a.equipped).map((la, index) => {
                  const mpCost = Math.max(1, Math.floor((battle.character?.mp || 100) * 0.10));
                  return (
                    <div
                      key={la.key}
                      className="ability-item"
                      onClick={() => {
                        safeAction({ type: 'learned_ability', abilityKey: la.key });
                        setActionMode('main');
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="ability-item-info">
                        <span className="ability-item-name" style={{ color: '#60a5fa' }}>🧠 {la.name}</span>
                        <span className="ability-item-desc">{la.damage} {la.element || la.type || ''} dmg • from {la.source}</span>
                      </div>
                      <div className="ability-item-cost">
                        <span className="mp-cost">{mpCost} MP</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CombatInterface;
